<script setup lang="ts">
import {
  YandexMap,
  YandexMapControls,
  YandexMapDefaultFeaturesLayer,
  YandexMapDefaultSchemeLayer,
  YandexMapListener,
  YandexMapZoomControl,
} from 'vue-yandex-maps'

import { UPDATE_AREAS } from '@/constants/delivery'

import { ServiceSettings } from '@/store/city/city.types'
import { useDeliveryStore } from '@/store/delivery/delivery'
import type { DeliveryDataAddress } from '@/store/delivery/delivery.types'
import { BalloonType, PlacesSearch } from '@/store/delivery/delivery.types'

import {
  AreaProperties,
  DragCoordinates,
  LatLng,
  PointProperties,
  PolygonProperties,
} from '@/api/modules/delivery/types'
import { Success } from '@/api/results'
import { TypesEntity } from '@/types/mapServices'
import {
  BehaviorMapEventHandler,
  DomEventHandler,
  LngLat,
  LngLatBounds,
  YMap,
  YMapControlsProps,
} from '@yandex/ymaps3-types'

import YandexMapClickedMarker from '@/components/Services/YandexMap/YandexMapClickedMarker.vue'
import YandexMapPoints from '@/components/Services/YandexMap/YandexMapPoints.vue'
import YandexMapPolygons from '@/components/Services/YandexMap/YandexMapPolygons.vue' /* eslint-disable */

import { debounce } from 'throttle-debounce'

type Props = {
  settings: ServiceSettings
  width: string
  areas: AreaProperties[]
  points: PointProperties[]
  clickedRestaurant: PointProperties | null
  selectedAddress: DeliveryDataAddress
}

const props = withDefaults(defineProps<Props>(), {
  areas: () => [],
  points: () => [],
  isDeliveryCourier: true,
})

const emit = defineEmits([
  'onClickToMap',
  'onClickRestaurant',
  'onUpdateCenterMap',
])

const deliveryStore = useDeliveryStore()

const { isMobileOrTablet } = useDevice()
const { $apiService } = useNuxtApp()

// Модель карты
const yandexMapData = shallowRef<null | YMap>(null)
// Координаты маркера на карте
const clickedMarkerCoordinates = ref<LngLat>([0, 0])
// Информация от полигона для маркера
const deliveryAddressDetails = ref<PolygonProperties | null>(null)

// Конфигурация настройки карты
const startSettings = {
  location: {
    center: [...props.settings.coords].reverse(),
    zoom: props.settings.zoom,
  },
  zoomRange: {
    min: 10,
    max: 20,
  },
}

const controlSettings: YMapControlsProps = {
  position: 'left bottom',
  orientation: 'vertical',
}

// Сетим данные, если выбран ресторан из списка
watch(
  () => props.clickedRestaurant,
  async (newPoint: PointProperties | null) => {
    if (!newPoint) return

    const { lon, lat } = newPoint.coordinates

    setLocationMap([lon, lat])

    // Динамически обновляем полигоны
    if (yandexMapData.value?.bounds) {
      await onUpdateAreas(yandexMapData.value?.bounds)
    }

    emit('onUpdateCenterMap')
  },
)

watch(
  () => props.selectedAddress?.coordinates,
  async (newCoordsOfAddress: LngLat) => {
    if (props.selectedAddress.address.place === PlacesSearch.MAP) return

    setLocationMap(newCoordsOfAddress)

    // Динамически обновляем полигоны
    if (yandexMapData.value?.bounds) {
      await onUpdateAreas(yandexMapData.value?.bounds, false)
    }

    updateCoordsOfMarker(newCoordsOfAddress)

    const deliveryInfo =
      await $apiService.geocode.getAddressInfoByCoordinates(newCoordsOfAddress)

    deliveryAddressDetails.value =
      deliveryInfo instanceof Success ? deliveryInfo.data : null

    // Обновляем дистанцию точек после выставления балуна
    updateDistance()
  },
)

// Метод зума карты по координатам
const setLocationMap = (coords: LngLat, zoom: number = 16) => {
  yandexMapData.value?.setLocation({
    center: coords,
    zoom,
  })
}

// Метод обновления данных о маркере
const updateCoordsOfMarker = (coords: LngLat) => {
  clickedMarkerCoordinates.value = coords
}

// Сбрасываем маркер c балуном
const resetBalloon = () => {
  updateCoordsOfMarker([0, 0])
  deliveryAddressDetails.value = null
}

const updateBalloon: DomEventHandler = (object, event) => {
  deliveryAddressDetails.value = object
    ? (object.entity.properties as unknown as PolygonProperties)
    : null

  updateCoordsOfMarker(event.coordinates)

  // Меняем центр карты по координатам маркера
  if (isMobileOrTablet) setLocationMap(event.coordinates)

  emit('onClickToMap', event.coordinates)
}

const onActionStart: BehaviorMapEventHandler = () => {
  emit('onUpdateCenterMap')
}

const onActionEnd: BehaviorMapEventHandler = (event) => {
  if (event.type !== 'drag' && event.type !== 'scrollZoom') {
    return
  }

  onUpdateAreas(event.location.bounds)
}

const getReverseCoords = (bounds: LngLatBounds) => {
  const lowerLeftBounds = bounds[0]
  const upperRightBounds = bounds[1]

  const coords: DragCoordinates = {
    lower_left: [lowerLeftBounds[1], lowerLeftBounds[0]],
    upper_right: [upperRightBounds[1], upperRightBounds[0]],
  }

  return coords
}

const debounceActionEnd = debounce(UPDATE_AREAS, onActionEnd)

const clickToMap: DomEventHandler = (object, event) => {
  switch (object?.type) {
    // Клик на хотспот
    case TypesEntity.HOTSPOT:
      updateBalloon(undefined, event)
      break
    // Клик по полигону
    case TypesEntity.FEATURE:
      updateBalloon(object, event)
      break
    // Клик на маркер
    case TypesEntity.MARKER:
      // Если активный маркер
      if (object.entity.properties?.markerOfClick) {
        resetBalloon()

        break
      }

      if (object.entity.properties) {
        emit('onClickRestaurant', object.entity.properties)

        setLocationMap(event.coordinates)
      }

      break
    default:
      updateBalloon(object, event)
      break
  }
}

// Удаляем карту из DOM
onBeforeUnmount(() => yandexMapData.value?.destroy())

const updateDistance = () => {
  if (clickedMarkerCoordinates.value[0] !== 0) {
    const reverseCoords: LatLng = [
      clickedMarkerCoordinates.value[1],
      clickedMarkerCoordinates.value[0],
    ]
    // Рассчитываем дистанцию от клика маркера до маркеров точек
    deliveryStore.setPointsDistance(reverseCoords)
  }
}

const onUpdateAreas = async (
  bounds: LngLatBounds,
  isNeedUpdateDistance = true,
) => {
  const coords = getReverseCoords(bounds)
  await deliveryStore.updateAreas(coords)

  if (isNeedUpdateDistance) {
    updateDistance()
  }
}

const listenerSettings = {
  onClick: clickToMap,
  onActionStart,
  onActionEnd: debounceActionEnd,
}
</script>

<template>
  <div>
    <yandex-map
      v-model="yandexMapData"
      :settings="startSettings"
      :width="width"
    >
      <yandex-map-default-scheme-layer />
      <yandex-map-default-features-layer />

      <yandex-map-listener :settings="listenerSettings" />

      <yandex-map-points
        v-if="points.length"
        :points="points"
        :clicked-restaurant="clickedRestaurant"
      />

      <yandex-map-polygons v-if="areas.length" :areas="areas" />

      <yandex-map-clicked-marker
        :delivery-address-details="deliveryAddressDetails"
        :clicked-marker-coordinates="clickedMarkerCoordinates"
        :selected-address="selectedAddress"
        :balloon-type="BalloonType.Delivery"
      />

      <yandex-map-controls :settings="controlSettings">
        <yandex-map-zoom-control />
      </yandex-map-controls>
    </yandex-map>
  </div>
</template>

<style scoped lang="scss">
:deep(.__ymap_container) {
  height: 100% !important;

  .ymaps3x0--map-container {
    border-radius: 10px !important;
  }
}

.yandex-map__mobile-point {
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -100%);
}
</style>
